import { useCallback, useMemo } from 'react'

import { z } from 'zod'

import { Page } from '@/components/layout/page/page.tsx'
import { Breadcrumbs } from '@/components/ui/breadcrumbs.tsx'
import { useToast } from '@/components/ui/toast'
import type { InferMessageEditorOutput } from '@/modules/message/components/editor/message-editor-context'
import { MessageEditorProvider } from '@/modules/message/components/editor/message-editor-context'
import type { DefaultMessageEditorValues } from '@/modules/message/components/editor/schema'
import { DefaultSchemaBase } from '@/modules/message/components/editor/schema'
import type { MessageBase } from '@/modules/message/components/sequence/message-sequence-editor-table'
import { MessageMediaQueryProvider } from '@/modules/message/query/message-media-query-context.tsx'
import { DefaultMessageMediaProvider } from '@/modules/message/service/message-fetch-context.tsx'
import { ScriptEditForm } from '@/modules/scripts/components/ScriptEditForm.tsx'
import type { ScriptNode } from '@/modules/scripts/types'
import { AppRoutes } from '@/utils/constants.ts'
import { useCreatorUrl } from '@/utils/creators.ts'

export interface ScriptEditView {
  mode: 'edit' | 'create'
  script?: ScriptNode
  isFetching?: boolean
  isSaving?: boolean
  onBack?: () => void
  onSave?: (script: EditorForm) => void
  error?: unknown
}

const EditorSchema = DefaultSchemaBase.extend({
  title: z
    .string({
      required_error: 'Title is required.',
    })
    .min(1, 'Title is required.')
    .max(255, 'Title is too long. Must be less than 256 characters.'),
  category: z
    .number()
    .optional()
    .transform((value, context) => {
      if (value === null || value === undefined) {
        context.addIssue({
          code: 'custom',
          message: 'Category is required.',
        })

        return z.NEVER
      }

      return z.number().nullish().optional().parse(value)
    }),
}).refine((data) => data.medias?.length || data.text?.length, {
  message: "Either 'text' or 'medias' must be provided.",
  path: ['medias', 'text'],
})

export type EditorForm = z.infer<typeof EditorSchema> & { text: string }

export const ScriptEditView = (props: ScriptEditView) => {
  return (
    <DefaultMessageMediaProvider>
      <MessageMediaQueryProvider>
        <ScriptEditViewContent {...props} />
      </MessageMediaQueryProvider>
    </DefaultMessageMediaProvider>
  )
}

const ScriptEditViewContent = (props: ScriptEditView) => {
  const toast = useToast()
  const { onBack, onSave, error, isSaving, mode, script } = props

  const handleSave = useCallback(
    (form: InferMessageEditorOutput<MessageBase>) => {
      try {
        const parsed = EditorSchema.parse(form)

        onSave?.({
          ...parsed,
          text: form.text ?? '',
        })
      } catch (error) {
        if (error instanceof z.ZodError) {
          toast.error(error.issues[0]?.message || 'Invalid form data')
        }
      }
    },
    [onSave, toast],
  )

  const defaultValues = useMemo<DefaultMessageEditorValues<EditorForm>>(() => {
    return {
      id: script?.id ?? null,
      title: script?.name ?? '',
      category: script?.categoryId ?? 0,
      text: script?.message?.text ?? '',
      fallback: script?.message?.fallback ?? null,
      fallbackVariable: script?.message?.fallbackVariable,
      enableFallbackVariable: script?.message?.enableFallbackVariable ?? false,
      medias: script?.message?.medias ?? [],
      price: script?.message?.price ?? 0,
    }
  }, [script])

  const listUrl = useCreatorUrl(AppRoutes.Creator.Settings.Scripts.List)

  return (
    <Page>
      <Breadcrumbs
        breadcrumbs={[
          { name: 'Scripts', to: listUrl },
          {
            name: mode === 'create' ? 'New Script' : 'Edit Script',
          },
        ]}
      />
      <MessageEditorProvider
        onSubmit={handleSave}
        // @ts-ignore
        defaultValues={defaultValues}
        textPlaceholder="Hey %nick%, how are you?"
      >
        <ScriptEditForm
          mode={mode}
          error={error}
          isSaving={isSaving}
          onBack={onBack}
        />
      </MessageEditorProvider>
    </Page>
  )
}
